import './barRaceDiagram.scss';

import cn from 'classnames';
import type { ECharts, EChartsOption } from 'echarts';
import React, { memo, useEffect, useRef } from 'react';

import { getEchartsModule } from 'shared/lib/getEchartsModule';

type ScaleMode = 'linear' | 'log';

type IBarRaceDiagramProps = {
  data: number[];
  barWidth: number;
  className?: string;
  scaleMode?: ScaleMode;
};

const scaleValue = (value: number, mode: ScaleMode): number => {
  if (mode === 'linear') {
    return value;
  }
  // Symmetric log scale: handles zero, small values, and negatives
  // log10(1 + |x|) ensures smooth behavior near zero
  // 0.65 factor reduces bar size by ~35%
  return Math.sign(value) * Math.log10(1 + Math.abs(value)) * 0.65;
};

const BarRaceDiagram: React.FC<IBarRaceDiagramProps> = ({
  data,
  barWidth,
  className,
  scaleMode = 'log',
  ...rest
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let chartInstance: ECharts | null = null;
    let resizeHandler: (() => void) | null = null;
    let isMounted = true;

    async function initializeChart() {
      if (!chartRef.current) return;
      const echarts = await getEchartsModule();
      if (!chartRef.current || !isMounted) return;

      chartInstance = echarts.init(chartRef.current);

      const option: EChartsOption = {
        animation: false,
        tooltip: {
          show: false,
        },
        grid: {
          top: 0,
          bottom: 0,
          left: 20,
          right: 30,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          position: 'bottom',
          splitLine: {
            show: true,
            lineStyle: {
              color: '#27272A',
              width: 1,
              type: 'solid',
            },
          },
          axisLabel: {
            show: false,
          },
          axisTick: { show: false },
        },
        yAxis: {
          type: 'category',
          axisLine: { show: false },
          axisLabel: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: false,
          },
          splitArea: {
            show: true,
            areaStyle: {
              color: ['transparent'],
            },
          },
        },
        // @ts-expect-error
        series: [
          {
            name: 'Cost',
            type: 'bar',
            stack: 'Total',
            barWidth,
            label: {
              show: true,
              position: 'right',
              formatter: params => {
                const originalValue = (params.data as { originalValue: number }).originalValue;
                return originalValue.toFixed(2);
              },
              align: 'left',
              color: '#fff',
              fontSize: 9,
              fontWeight: 400,
            },
            data: data.map(value => ({
              value: scaleValue(value, scaleMode),
              originalValue: value,
              label: {
                position: 'inside',
                distance: value < 0 ? 20 : 5,
                color: '#fff',
                fontSize: 14,
                fontWeight: 500,
              },
              itemStyle: {
                color: value < 0 ? '#f23645' : '#089981',
                borderRadius: value < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0],
              },
              emphasis: {
                itemStyle: {
                  scale: 1.1,
                },
                label: {
                  color: '#FFFFFF',
                  fontWeight: 500,
                },
              },
            })),
            emphasis: {
              focus: 'none',
            },
          },
          {
            name: 'Center Line',
            type: 'line',
            markLine: {
              silent: true,
              symbol: 'none',
              label: {
                show: false,
              },
              lineStyle: {
                color: 'transparent',
                width: 1,
                type: 'solid',
              },
              data: [{ xAxis: 0 }],
            },
          },
        ],
      };

      chartInstance.setOption(option);

      resizeHandler = () => {
        chartInstance?.resize();
      };

      window.addEventListener('resize', resizeHandler);
    }

    initializeChart();

    return () => {
      isMounted = false;
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      chartInstance?.dispose();
    };
  }, [data, barWidth, scaleMode]);

  return (
    <div className={cn({ [className ?? '']: !!className }, 'mm-bar-race-diagram-container')}>
      <div ref={chartRef} className="mm-bar-race-diagram" {...rest} />
    </div>
  );
};

const memoBarRaceDiagram = memo(BarRaceDiagram);

export { memoBarRaceDiagram as BarRaceDiagram };
